import { useId } from 'react';
import { Box, Checkbox, CircularProgress, FormControlLabel, FormHelperText } from '@mui/material';
import { useAddServiceToRealmForm } from 'RealmModule/hooks/useAddServiceToRealmForm';
import RealmSelector from 'SandboxModule/components/RealmSelector';
import { OverrideItem } from 'TenantModule/components/Management/IntegrationAgentsNameSelector';
import { ServiceNameSelector } from 'TenantModule/components/Management/ServiceNameSelector';
import { TenantRealmInfo } from 'TenantModule/components/TenantRealmInfo';
import { TenantSelector } from 'TenantModule/components/TenantSelector';
import { MaskedInput } from 'components/MaskedInput';
import { Divider } from 'components/material-ui-styles';
import { StyledDefaultPrimaryButton } from 'styles';
import { Realm } from 'types';

export const AddServiceToRealmForm: React.FC<{
  disableSelectTenantAndRealm?: boolean;
  realmId?: Realm['realm_id'];
  /** Called after the service is successfully added to the realm. */
  onSuccess?: VoidFunction;
  /**
   * Restricts the service picker to these items instead of everything from
   * /service/integrationDisplayNames (e.g. only draft-capable services when
   * embedded in the Create Draft modal).
   */
  serviceItemsOverride?: OverrideItem[];
}> = ({ disableSelectTenantAndRealm, realmId, onSuccess, serviceItemsOverride }) => {
  // The form can be mounted more than once (page card + stacked modal), so
  // the checkbox/helper-text association needs a unique id per instance.
  const firstRealmHelperTextId = useId();
  const {
    onSubmitRealmAddServiceForm,
    onAddRealmServiceFormChange,
    selectedTenant,
    onTenantSelectorClick,
    onRealmSelectorClick,
    selectedRealm,
    realmDropdownRef,
    addRealmServiceParams,
    isAddingServiceToRealm,
    isFetchingSandboxes,
  } = useAddServiceToRealmForm({ realmId, onSuccess });

  // With a pinned realm, submitting before the realm resolves would silently
  // no-op (the submit handler guards on selectedRealm) — keep the form
  // disabled until it's there.
  const isRealmUnresolved = Boolean(disableSelectTenantAndRealm) && !selectedRealm;
  const isFormDisabled = isAddingServiceToRealm || isRealmUnresolved;

  return (
    <form onSubmit={onSubmitRealmAddServiceForm}>
      <h3>Add Service To Realm</h3>
      <Box gap={12} display={'flex'} flexDirection={'column'}>
        {disableSelectTenantAndRealm ? (
          selectedRealm ? (
            <TenantRealmInfo
              // The realm's own tenant — the pinned realm may not belong to
              // the globally selected tenant. Same fallback as the submit
              // payload so the display always matches what gets sent.
              tenantName={selectedRealm.tenant || selectedTenant?.tenantName}
              realmId={selectedRealm.realm_id}
              realmName={selectedRealm.team_name}
            />
          ) : isFetchingSandboxes === false ? (
            // The lookup finished without finding the pinned realm.
            <Box>Couldn&apos;t load this realm&apos;s info — close and try again.</Box>
          ) : (
            <Box display="flex" alignItems="center" gap={2}>
              <CircularProgress size={16} /> Loading realm info…
            </Box>
          )
        ) : (
          <>
            {selectedTenant ? (
              <TenantSelector
                onClick={onTenantSelectorClick}
                tenantId={selectedTenant.id}
                helperText="Tenant that owns the realm."
              />
            ) : (
              <></>
            )}
            <RealmSelector
              onClick={onRealmSelectorClick}
              realmId={selectedRealm?.realm_id || ''}
              ref={realmDropdownRef}
              tooltip="Realm to add the service to."
            />
          </>
        )}
        <ServiceNameSelector
          value={addRealmServiceParams.integrationAgentName || ''}
          onChange={onAddRealmServiceFormChange}
          disabled={isFormDisabled}
          itemsOverride={serviceItemsOverride}
          helperText="Service to add — Galaxy creates its database, permissions, and secret."
        />
        <MaskedInput
          label="Admin Database Credential Secret (optional)"
          name="adminDatabaseCredentialSecret"
          value={addRealmServiceParams.adminDatabaseCredentialSecret}
          onChange={onAddRealmServiceFormChange}
          variant="outlined"
          disabled={isFormDisabled}
          helperText="Secrets Manager secret used to create the service's database — leave blank to use the Realm or Tenant default."
        />
        {/* Always-visible helper text instead of a tooltip: an interactive
            trigger inside the <label> would toggle the checkbox when clicked,
            and an icon-only trigger is invisible to screen readers. */}
        <Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={addRealmServiceParams.isFirstRealm}
                onChange={onAddRealmServiceFormChange}
                name="isFirstRealm"
                color="primary"
                disabled={isFormDisabled}
                inputProps={{ 'aria-describedby': firstRealmHelperTextId }}
              />
            }
            label="First Realm (optional)"
          />
          <FormHelperText id={firstRealmHelperTextId} sx={{ mt: -0.5 }}>
            Check only when this is the service&apos;s first realm on the cluster — Galaxy then
            skips the service refresh and initialization calls that assume the service is already
            deployed.
          </FormHelperText>
        </Box>
      </Box>
      <Divider sx={{ my: 6 }} />
      <StyledDefaultPrimaryButton type="submit" disabled={isFormDisabled}>
        Submit
      </StyledDefaultPrimaryButton>
    </form>
  );
};
